import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../state/app.state';
import { projectDetails } from '../../../../core/projects.selectors';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { FormGroup } from '@angular/forms';
import { linkRawValue, recordLinkPayload } from '../../../../../../shared/helpers/design-context';
import { relationId } from './report-line-item-ids.utils';
import type { FeatureSource } from '../../../../../../shared/models/control-form';
import type { MapFeatureLink } from '../../../../../../shared/models/map-feature-link';
import {
  isTimeField,
  toStoredTime,
} from './daily-report-components/detail-components/generic-form/form-blocks.util';

/** See DailyReportService.formPickerContext. */
export interface FormPickerContext {
  featureSource: FeatureSource | null;
  existingLink: MapFeatureLink | null;
  latitude: number | string | null;
  longitude: number | string | null;
}

@Injectable({
  providedIn: 'root',
})
export class DailyReportService implements OnDestroy {

  stationAdded: Subject<any> = new Subject();
  triggerSave: Subject<any> = new Subject();
  save: Subject<any> = new Subject();
  addComment: Subject<any> = new Subject();
  fetchComments: Subject<void> = new Subject();
  commentsValue: Subject<string> = new Subject();
  disableAddMenu: Subject<any> = new Subject();
  disableOptionMenu: Subject<any> = new Subject();
  hideDeleteOption: Subject<any> = new Subject();
  fetchDailyReportById: Subject<{ fromDialog?: boolean }> = new Subject<{ fromDialog?: boolean }>();
  issue: Subject<any> = new Subject();
  add: Subject<void> = new Subject<void>();
  triggerDelete: Subject<any> = new Subject();
  delete: Subject<any> = new Subject();
  selectedLocation: Subject<any> = new Subject();
  /**
   * The open form editor's Location pin context, for pickers opened outside it (the action bar "+ Add location"):
   * the form's feature_source and the record's link/pin. Set by the editor while it is open; null otherwise.
   */
  formPickerContext: (() => FormPickerContext) | null = null;
  openDetailsPanel: Subject<any> = new Subject();
  upDateGeneralFormSummary: Subject<any> = new Subject();
  updateFrequencyQuestionValue: Subject<any> = new Subject();
  setSignature: Subject<any> = new Subject();
  setStationRange: Subject<any> = new Subject();
  itdToDelete: Subject<any> = new Subject();
  videoToBeDeletedFromMuxDb: Subject<any> = new Subject();
  addNewStation: Subject<any> = new Subject();
  newStationSelected: Subject<any> = new Subject();
  addNewTimeToStation: Subject<any> = new Subject();
  deleteStation: Subject<any> = new Subject();
  deleteTimeFromStation: Subject<any> = new Subject();
  setItType: Subject<any> = new Subject();
  stationsList: Subject<any> = new Subject();
  locationStationToolbarState: any = null;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    public dialog: MatDialog,
    public route: ActivatedRoute,
  ) {

  }

  formatAndSaveMultilineBidItemPayload(form, bidItem, lineItems) {
    // A link removed this session goes out as { sheet: null }; an untouched null as a bare null (R54)
    const values = linkRawValue(form);
    const lineItemsPayload = lineItems.map(lineItem => this._createBidItemPayload(bidItem, lineItem, values));

    let hasEmpty = false;

    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < lineItemsPayload.length; i++) {
      const lineItem = lineItemsPayload[i];

      if (!lineItem.sources.length && !lineItem.geometry?.features?.length && lineItem.field_values) {
        // tslint:disable-next-line:prefer-for-of
        for (let j = 0; j < lineItem.field_values.length; j++) {
          if (!lineItem.quantity && lineItem.field_values[j].value === null) {
            hasEmpty = true;
            break;
          }
        }
      }

      if (hasEmpty) {
        break;
      }
    }

    if (hasEmpty) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: new ConfirmDialogModel(
          'Empty line items',
          `There are empty entries, which will be removed if you continue. Do you want to continue?`,
          'Continue',
          'Cancel',
        ),
      });

      dialogRef.afterClosed().subscribe((response) => {
        if (response) {
          const cleanedLineItemsPayload = lineItemsPayload.filter(lineItem => {

            if (!lineItem.sources.length && !lineItem.geometry?.features?.length && lineItem.field_values) {
              // tslint:disable-next-line:prefer-for-of
              for (let j = 0; j < lineItem.field_values.length; j++) {
                if (!lineItem.quantity && lineItem.field_values[j].value === null) {
                  return false;
                }
              }
            }

            return true;
          });

          this.save.next({
            type: 'bidItems',
            data: cleanedLineItemsPayload,
            options: {
              bidItem,
            },
          });
        }
      });
    } else {
      this.save.next({
        type: 'bidItems',
        data: lineItemsPayload,
        options: {
          bidItem,
        },
      });
    }
  }

  formatDeliveriesAndUsages(materialDeliveries) {
    const categorization = {
      deliveryBidItems: [],
      deliveryMaterials: [],
      deliveryBidItemMaterials: [],
      usageBidItems: [],
      usageMaterials: [],
      usageBidItemMaterials: [],
    };

    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < materialDeliveries.length; i++) {
      const element = materialDeliveries[i];
      if (
        element.bid_item !== null &&
        element.material === null &&
        element.received === null &&
        element.used === null &&
        element.quantity > 0
      ) {
        categorization.deliveryBidItems.push(element);
        continue;
      }

      if (
        element.bid_item === null &&
        element.material !== null &&
        element.received !== null &&
        element.used === null &&
        element.quantity === 0
      ) {
        categorization.deliveryMaterials.push(element);
        continue;
      }

      if (
        element.bid_item !== null &&
        element.material !== null &&
        element.received !== null &&
        element.used === null &&
        element.quantity === 0
      ) {
        categorization.deliveryBidItemMaterials.push(element);
        continue;
      }

      // Add Header items to both the att bid items with materials
      if (
        element.bid_item !== null &&
        (element.material !== null || true) &&
        element.received === null &&
        element.used === null &&
        element.quantity === 0
      ) {
        categorization.deliveryBidItemMaterials.push(element);
        categorization.usageBidItemMaterials.push(element);
        continue;
      }

      if (
        element.bid_item !== null &&
        element.material === null &&
        element.received === null &&
        element.used !== null &&
        element.quantity === 0
      ) {
        categorization.usageBidItems.push(element);
        continue;
      }

      if (
        element.bid_item === null &&
        element.material !== null &&
        element.received === null &&
        element.used !== null &&
        element.quantity === 0
      ) {
        categorization.usageMaterials.push(element);
        continue;
      }

      if (
        element.bid_item !== null &&
        element.material !== null &&
        element.received === null &&
        element.used !== null &&
        element.quantity === 0
      ) {
        categorization.usageBidItemMaterials.push(element);
      }
    }

    const deliveryAndUsage = [];

    // tslint:disable-next-line:forin
    for (const key in categorization) {
      const eachCategoryData = categorization[key];
      deliveryAndUsage.push(...this._createDeliveryAndUsageBlocks(eachCategoryData, key));
    }

    return {
      deliveries: deliveryAndUsage.filter(o => o.type === 'delivery'),
      usages: deliveryAndUsage.filter(o => o.type === 'usage'),
    };
  }

  createNewDeliveryBlock(data) {
    let customFields: any[];
    this.store.select(projectDetails)
      .subscribe(project => {
        customFields = project.delivery_tracking_fields;
      }).unsubscribe();

    if (data.track_material_quantity !== undefined) {
      // This is a bid item
      if (data.materials.length) {
        // this is a bid item with materials
        return {
          id: `new_${new Date().getTime()}`,
          heading: data.item,
          subheading: data.description,
          latitude: null,
          longitude: null,
          location: null,
          station_range: null,
          map_feature_link: null,
          pictures: [],
          comment: null,
          type: 'delivery',
          subType: 'bidItemWithMaterials',
          initialState: {
            bid_item: data,
            bid_item_id: data.id,
          },
          materials: [],
        };
      } else {
        // This is a bid item without materials
        return {
          id: `new_${new Date().getTime()}`,
          heading: data.item,
          subheading: data.description,
          uom: data.uom,
          quantity: null,
          fieldValues: customFields.map(o => ({ id: o.id, value: null })),
          latitude: null,
          longitude: null,
          location: null,
          station_range: null,
          map_feature_link: null,
          pictures: [],
          comment: null,
          type: 'delivery',
          subType: 'onlyBidItem',
          initialState: {
            bid_item: data,
            bid_item_id: data.id,
          },
        };
      }
    } else {
      // This is material
      if (data.track_received) {
        return {
          id: `new_${new Date().getTime()}`,
          heading: data.name,
          uom: data.uom,
          quantity: null,
          fieldValues: customFields.map(o => ({ id: o.id, value: null })),
          latitude: null,
          longitude: null,
          location: null,
          station_range: null,
          map_feature_link: null,
          pictures: [],
          comment: null,
          type: 'delivery',
          subType: 'onlyMaterial',
          initialState: { material: data },
        };
      }
    }
  }

  formatAndSaveDelivery(values, data, linesToDelete = []) {
    let customFields: any[];
    this.store.select(projectDetails)
      .subscribe(project => {
        customFields = project.delivery_tracking_fields;
      }).unsubscribe();

    let response = [];

    if (data.subType === 'onlyBidItem') {
      response = [
        {
          id: data.id,
          bid_item_id: relationId(data.initialState, 'bid_item'),
          latitude: values[data.id].latitude,
          longitude: values[data.id].longitude,
          location: values[data.id]?.location || null,
          station_range: values[data.id]?.station_range || null,
          map_feature_link: this._linePayloadLink(values[data.id]),
          pictures: JSON.parse(values[data.id].pictures),
          videos: JSON.parse(values[data.id].videos),
          quantity: values[data.id].quantity,
          received: null,
          used: null,
          field_values: customFields.map(o => ({ id: o.id, value: values[data.id]?.[o.id] || null })),
          comment: values[data.id].comment || '',
        },
      ];
    } else if (data.subType === 'onlyMaterial') {
      response = [
        {
          id: data.id,
          material_id: relationId(data.initialState, 'material'),
          latitude: values[data.id]?.latitude,
          longitude: values[data.id].longitude,
          location: values[data.id]?.location || null,
          station_range: values[data.id]?.station_range || null,
          map_feature_link: this._linePayloadLink(values[data.id]),
          pictures: JSON.parse(values[data.id].pictures),
          videos: JSON.parse(values[data.id].videos),
          received: values[data.id].quantity,
          quantity: 0,
          used: null,
          field_values: customFields.map(m => ({ id: m.id, value: values[data.id]?.[m.id] || null })),
          comment: values[data.id].comment || '',
        },
      ];
    } else if (data.subType === 'bidItemWithMaterials') {
      response = data.materials.map(o => ({
        id: o.id,
        bid_item_id: relationId(o.initialState, 'bid_item') || relationId(data.initialState, 'bid_item'),
        material_id: relationId(o.initialState, 'material'),
        latitude: values[o.id]?.latitude,
        longitude: values[o.id].longitude,
        location: values[o.id]?.location || null,
        station_range: values[o.id]?.station_range || null,
        map_feature_link: this._linePayloadLink(values[o.id]),
        pictures: JSON.parse(values[o.id].pictures),
        videos: JSON.parse(values[data.id].videos),
        received: values[o.id].quantity,
        quantity: 0,
        used: null,
        field_values: customFields.map(m => ({ id: m.id, value: values[o.id]?.[m.id] || null })),
        comment: values[o.id].comment || '',
      }));

      response.push({
        id: data.id,
        bid_item_id: relationId(data.initialState, 'bid_item'),
        latitude: values[data.id]?.latitude,
        longitude: values[data.id].longitude,
        location: values[data.id]?.location || null,
        station_range: values[data.id]?.station_range || null,
        map_feature_link: this._linePayloadLink(values[data.id]),
        pictures: JSON.parse(values[data.id].pictures),
        videos: JSON.parse(values[data.id].videos),
        quantity: 0,
        received: null,
        used: null,
        comment: values[data.id].comment || '',
      });
    }

    this.save.next({
      type: 'delivery',
      data: response,
      options: {
        linesToDelete,
      },
    });
  }

  createNewUsageBlock(data) {
    if (data.track_material_quantity !== undefined) {
      // This is a bid item
      if (data.materials.length) {
        // this is a bid item with materials
        return {
          id: `new_${new Date().getTime()}`,
          heading: data.item,
          subheading: data.description,
          latitude: null,
          longitude: null,
          location: null,
          station_range: null,
          pictures: [],
          comment: null,
          type: 'usage',
          subType: 'bidItemWithMaterials',
          initialState: {
            bid_item: data,
            bid_item_id: data.id,
          },
          materials: [],
        };
      } else {
        // This is a bid item without materials
        return {
          id: `new_${new Date().getTime()}`,
          heading: data.item,
          subheading: data.description,
          uom: data.uom,
          quantity: null,
          latitude: null,
          longitude: null,
          location: null,
          station_range: null,
          pictures: [],
          comment: null,
          type: 'usage',
          subType: 'onlyBidItem',
          initialState: {
            bid_item: data,
            bid_item_id: data.id,
          },
        };
      }
    } else {
      // This is material
      if (data.track_used) {
        return {
          id: `new_${new Date().getTime()}`,
          heading: data.name,
          uom: data.uom,
          quantity: null,
          latitude: null,
          longitude: null,
          location: null,
          station_range: null,
          pictures: [],
          comment: null,
          type: 'usage',
          subType: 'onlyMaterial',
          initialState: { material: data },
        };
      }
    }
  }

  formatAndSaveUsage(values, data, linesToDelete = []) {
    let response = [];

    if (data.subType === 'onlyBidItem') {
      response = [
        {
          id: data.id,
          bid_item_id: relationId(data.initialState, 'bid_item'),
          latitude: values[data.id].latitude,
          longitude: values[data.id].longitude,
          location: values[data.id]?.location || null,
          station_range: values[data.id]?.station_range || null,
          pictures: JSON.parse(values[data.id].pictures),
          videos: JSON.parse(values[data.id].videos),
          quantity: values[data.id].quantity,
          received: null,
          used: null,
          comment: values[data.id].comment || '',
        },
      ];
    } else if (data.subType === 'onlyMaterial') {
      response = [
        {
          id: data.id,
          material_id: relationId(data.initialState, 'material'),
          latitude: values[data.id]?.latitude,
          longitude: values[data.id].longitude,
          location: values[data.id]?.location || null,
          station_range: values[data.id]?.station_range || null,
          pictures: JSON.parse(values[data.id].pictures),
          videos: JSON.parse(values[data.id].videos),
          received: null,
          quantity: 0,
          used: values[data.id]?.quantity,
          comment: values[data.id]?.comment || '',
        },
      ];
    } else if (data.subType === 'bidItemWithMaterials') {
      response = data.materials.map(o => ({
        id: o.id,
        bid_item_id: relationId(o.initialState, 'bid_item') || relationId(data.initialState, 'bid_item'),
        material_id: relationId(o.initialState, 'material'),
        latitude: values[o.id]?.latitude,
        longitude: values[o.id].longitude,
        location: values[o.id]?.location || null,
        station_range: values[o.id]?.station_range || null,
        pictures: JSON.parse(values[o.id].pictures),
        videos: JSON.parse(values[data.id].videos),
        received: null,
        quantity: 0,
        used: values[o.id].quantity,
        comment: values[o.id].comment || '',
      }));

      response.push({
        id: data.id,
        bid_item_id: relationId(data.initialState, 'bid_item'),
        latitude: values[data.id]?.latitude,
        longitude: values[data.id].longitude,
        location: values[data.id]?.location || null,
        station_range: values[data.id]?.station_range || null,
        pictures: JSON.parse(values[data.id].pictures),
        videos: JSON.parse(values[data.id].videos),
        quantity: 0,
        received: null,
        used: null,
        comment: values[data.id].comment || '',
      });
    }

    this.save.next({
      type: 'usage',
      data: response,
      options: {
        linesToDelete,
      },
    });
  }

  formatBidItems(bidItemsList) {
    const bidItems: any = {};

    for (const currentBidItem of bidItemsList) {
      if (bidItems[`${currentBidItem.bid_item.id}_${currentBidItem.heading_id}`] === undefined) {
        bidItems[`${currentBidItem.bid_item.id}_${currentBidItem.heading_id}`] = [currentBidItem];
      } else {
        bidItems[`${currentBidItem.bid_item.id}_${currentBidItem.heading_id}`].push(currentBidItem);
      }
    }

    const bidItemsArr = [];

    for (const key in bidItems) {
      if (bidItems.hasOwnProperty(key)) {
        bidItemsArr.push({
          id: bidItems?.[key]?.[0]?.bid_item?.id,
          bidItem: bidItems?.[key]?.[0]?.bid_item,
          heading: bidItems?.[key]?.[0]?.heading,
          ...(bidItems?.[key]?.[0]?.bid_item?.record_by_station ?
            { stations: bidItems?.[key] } :
            { lineItems: bidItems?.[key] }),
        });
      }
    }

    return bidItemsArr;
  }

  formatLabor(labors) {
    if (labors.length) {
      const reduced = labors.reduce((acc, current) => {

        if (acc[current.type]) {
          acc[current.type].quantity = acc[current.type].quantity + (current.quantity || 0);
          acc[current.type].time = acc[current.type].time + (current.duration || 0);
          acc[current.type].idleTime = acc[current.type].idleTime + (current.idle_duration || 0);
          acc[current.type].equipmentList.push(current);
        } else {
          acc[current.type] = {
            id: new Date().getTime(),
            type: current.type,
            quantity: current.quantity || 0,
            time: current.duration || 0,
            idleTime: current.idle_duration || 0,
            newLaborFormat: current.new_labor_format,
            equipmentList: [current],
          };
        }

        return acc;
      }, {});

      return Object.values(reduced);
    }
    return [];
  }

  formatEquipment(equipments) {
    if (equipments.length) {
      const reduced = equipments.reduce((acc, current) => {

        if (acc[current.type]) {
          acc[current.type].quantity = acc[current.type].quantity + (current.quantity || 0);
          acc[current.type].time = acc[current.type].time + (current.duration || 0);
          acc[current.type].idleTime = acc[current.type].idleTime + (current.idle_duration || 0);
          acc[current.type].equipmentList.push(current);
        } else {
          acc[current.type] = {
            id: new Date().getTime(),
            type: current.type,
            quantity: current.quantity || 0,
            time: current.duration || 0,
            idleTime: current.idle_duration || 0,
            newLaborFormat: current.new_labor_format,
            equipmentList: [current],
          };
        }

        return acc;
      }, {});

      return Object.values(reduced);
    }

    return [];
  }

  /**
   * Serialize a single captured signer object into the saved payload shape,
   * preserving method/font/signed_at/drawn-image pictures. Pure helper.
   */
  private static serializeSignature(sig: any): any {
    return {
      role: sig?.role ?? null,
      signature_text: sig?.signature_text ?? null,
      signature_method: sig?.signature_method || 'typed',
      ...(sig?.signature_font ? { signature_font: sig.signature_font } : {}),
      ...(sig?.signed_at ? { signed_at: sig.signed_at } : {}),
      ...(sig?.pictures?.length ? { pictures: sig.pictures } : {}),
    };
  }

  /**
   * Build the saved `signatures` payload for a form.
   *
   * Primary path: serialize the dynamic `signatures[]` array captured by the
   * component (an arbitrary number of signers; roles may repeat). Each entry
   * carries its own role/method/font/signed_at/drawn-image pictures.
   *
   * Backward-compatible legacy fallback: when the dynamic array is empty but the
   * old printed-name controls (signatureOne/Two) hold text, emit the legacy
   * fixed QA/QC typed pair so older forms still save. Returns `null` when there
   * is no signature content at all (so the form clears signatures on save).
   *
   * Static + pure (no `this`) so it is unit-testable in isolation.
   */
  static buildSignaturesPayload(signatureOneText: any, signatureTwoText: any, signatures: any[] = []): any[] | null {
    const list = Array.isArray(signatures) ? signatures : [];

    const isPopulated = (sig: any) =>
      !!(sig?.signature_text || sig?.pictures?.length || sig?.signature_method);

    const populated = list.filter(isPopulated);

    // Primary path: dynamic signers captured via the dialog.
    if (populated.length) {
      return populated.map(sig => DailyReportService.serializeSignature(sig));
    }

    // Legacy fallback: no dynamic signers, but the printed-name controls hold
    // text. Preserve the historical fixed QA/QC typed pair.
    if (signatureOneText || signatureTwoText) {
      const legacy = [
        { role: 'QA', signature_text: signatureOneText || null },
        { role: 'QC', signature_text: signatureTwoText || null },
      ];
      return legacy.map(sig => DailyReportService.serializeSignature(sig));
    }

    return null;
  }

  formatForms(formsForm: FormGroup, headerFields: any, lineFields: any, dewcForm: any, data: any, signatures: any[] = []) {
    const formsData = linkRawValue(formsForm);
    const tempLinesData = [];

    const allDependentFieldNames = new Set<string>();

    const collectDependentFields = (fields: any[]) => {
      fields?.forEach((field) => {
        if (Array.isArray(field?.dependent_fields) && field?.dependent_fields?.length) {
          field.dependent_fields.forEach((dep: any) => {
            allDependentFieldNames.add(dep.name);
          });
        }
      });
    };

    collectDependentFields(lineFields || []);

    const allDependentFieldNamesArray = [...allDependentFieldNames];

    const allDependentFieldIdsArray = lineFields
      .filter(field => allDependentFieldNamesArray.includes(field.name))
      .map(field => field.id);

    if (dewcForm) {
      Object.entries(formsData)
        .filter(([key]) => key.startsWith('section_'))
        .forEach(([sectionKey, sectionValue]) => {
          Object.entries(sectionValue).forEach(([lineKey, lineData]) => {
            tempLinesData.push({
              comment: lineData.comment || null,
              pictures: JSON.parse(lineData?.pictures || '[]'),
              videos: JSON.parse(lineData?.videos || '[]'),
              field_values: lineFields?.map((lf: any) => {
                let value = lineData[lf.name] ?? null;

                // Handle object { value: something }
                if (typeof value === 'object' && value?.value) {
                  value = value?.value;
                }

                return { id: lf.id, value };
              }),
            });
          });
        });

      tempLinesData.forEach(obj => {
        obj.field_values = obj.field_values.filter(
          o => o?.value !== null && !allDependentFieldIdsArray.includes(o.id),
        );
      });
    } else {
      Object.entries(formsData)
        .filter(([key]) => key.startsWith('line_'))
        .sort(([aKey], [bKey]) => {
          const aNum = parseInt(aKey.split('_')[1], 10);
          const bNum = parseInt(bKey.split('_')[1], 10);
          return aNum - bNum;
        })
        .forEach(([lineKey, lineData]: [string, any]) => {
          tempLinesData.push({
            comment: lineData.comment || null,
            pictures: JSON.parse(lineData.pictures || '[]'),
            videos: JSON.parse(lineData.videos || '[]'),
            field_values: lineFields.map((lf: any) => {
              let value = lineData[lf.name] ?? null;

              // Handle object { value: something }
              if (typeof value === 'object' && value?.value) {
                value = value.value;
              }

              // Handle hh:mm:ss time format
              if (typeof value === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(value)) {
                const [hours, minutes, seconds] = value.split(':');
                value = { hours, minutes, seconds };
              }

              return { id: lf.id, value };
            }),
          });
        });
    }

    Object.entries(formsData.questions).forEach(([questionId, questionData]) => {
      tempLinesData.push({
        comment: questionData['comment'],
        pictures: JSON.parse(questionData['pictures'] || '[]'),
        videos: JSON.parse(questionData['videos'] || '[]'),
        field_values: [
          {
            id: questionId,
            value: questionData['field_values'],
          },
        ],
      });
    });

    formsData?.frequency?.forEach((frequency) => {
      Object.entries(formsData?.[`${frequency}_questions`]).forEach(([questionId, questionData]) => {
        tempLinesData.push({
          comment: questionData['comment'],
          pictures: JSON.parse(questionData['pictures'] || '[]'),
          videos: JSON.parse(questionData['videos'] || '[]'),
          field_values: [
            {
              id: questionId,
              value: questionData['field_values'],
            },
          ],
        });
      });
    });

    const pictures = JSON.parse(formsData.pictures || '[]');
    const videos = JSON.parse(formsData.videos || '[]');

    // tslint:disable-next-line:variable-name
    const field_values = [];
    headerFields?.forEach(field => {
      if (formsData[field.name] === '_others') {
        field_values.push({
          id: field.id,
          value: formsData[field.name + '_others'],
        });
      } else {
        field_values.push({
          id: field.id,
          value: formsData[field.name],
        });
      }
    });
    const payload = {
      ...(data ? { id: data?.id } : { id: `new_${new Date()}` }),
      control_form_id: formsData['formType'],
      lines: lineFields?.length ? tempLinesData : this.removeEmptyLineData(tempLinesData),
      comment: formsData['comment'],
      latitude: formsData['latitude'],
      longitude: formsData['longitude'],
      // Linked at the form header (report_form_header), never per line.
      map_feature_link: this._linePayloadLink(formsData),
      pictures,
      videos,
      signatures: DailyReportService.buildSignaturesPayload(
        formsData['signatureOne'],
        formsData['signatureTwo'],
        signatures,
      ),
      station_range: {
        from: {
          station: formsData['stationRangeFromStation'],
          offset_type: formsData['stationRangeFromOffsetType'],
          offset_distance: formsData['stationRangeFromOffsetDistance'],
        },
        to: {
          station: formsData['stationRangeToStation'],
          offset_type: formsData['stationRangeToOffsetType'],
          offset_distance: formsData['stationRangeToOffsetDistance'],
        },
        alignment: formsData['alignment'],
      },
      field_values,
    };

    this.save.next({
      type: 'forms',
      data: [payload],
    });
  }

  formatTestingForms(
    testingFormsForm: FormGroup,
    headerFields,
    footerFields,
    lineFields,
    data,
    enableNestedAccordion,
    signatures: any[] = [],
  ) {
    const formData = linkRawValue(testingFormsForm);

    const allDependentFieldNames = new Set<string>();

    const collectDependentFields = (fields: any[]) => {
      fields?.forEach((field) => {
        if (Array.isArray(field.dependent_fields) && field.dependent_fields.length) {
          field.dependent_fields.forEach((dep: any) => {
            allDependentFieldNames.add(dep.name);
          });
        }
      });
    };

    collectDependentFields(headerFields);
    collectDependentFields(footerFields);
    collectDependentFields(lineFields?.fields || []);

    const allDependentFieldNamesArray = [...allDependentFieldNames];

    const headerFieldValues = headerFields
      .filter(field => !allDependentFieldNamesArray.includes(field.name))
      .map(field => ({
        id: field.id,
        value: formData[field.name],
      }));

    const footerFieldValues = footerFields
      .filter(field => !allDependentFieldNamesArray.includes(field.name))
      .map(field => ({
        id: field.id,
        value: formData[field.name],
      }));

    const pictures = JSON.parse(formData.pictures || '[]');
    const videos = JSON.parse(formData.videos || '[]');

    const groupByFields = lineFields?.group_by || [];
    const lineFormData = [];
    let lines: any;

    if (enableNestedAccordion) {
      Object.keys(formData).forEach((key) => {
        if (key.startsWith('line_')) {
          const lineEntry = formData[key];

          const groupFields = {};
          groupByFields.forEach((fieldName) => {
            groupFields[fieldName] = lineEntry[fieldName];
          });

          Object.keys(lineEntry).forEach((fieldKey) => {
            if (fieldKey.startsWith('time_')) {
              const timeBlock = lineEntry[fieldKey];

              lineFormData.push({
                ...groupFields,
                ...timeBlock,
              });
            }
          });
        }
      });

      const cleanedLineFormData = lineFormData?.map(entry => {
        // @ts-ignore
        return Object.fromEntries(
          Object.entries(entry).filter(([key]) => !allDependentFieldNamesArray.includes(key)),
        );
      });

      const fieldMap = {};
      lineFields.fields.forEach((field) => {
        fieldMap[field.name.toLowerCase()] = field.id;
      });

      lines = cleanedLineFormData?.map((entry) => {
        // tslint:disable-next-line:variable-name
        const field_values = [];

        Object.entries(entry).forEach(([key, value]) => {
          const id = fieldMap[key.toLowerCase()];
          if (id) {
            field_values.push({ id, value });
          }
        });

        return {
          field_values,
          pictures: JSON.parse((entry.pictures as string) || '[]'),
          videos: JSON.parse((entry.videos as string) || '[]'),
        };
      });
    } else {
      Object.keys(formData).forEach((key) => {
        if (key.startsWith('line_')) {
          const lineEntry = formData[key];
          lineFormData.push(lineEntry);
        }
      });

      const cleanedLineFormData = lineFormData?.map(entry => {
        // @ts-ignore
        return Object.fromEntries(
          Object.entries(entry).filter(([key]) => !allDependentFieldNamesArray.includes(key)),
        );
      });

      const fieldMap = {};
      lineFields.fields.forEach((field) => {
        fieldMap[field.name.toLowerCase()] = field.id;
      });

      lines = cleanedLineFormData?.map((entry) => {
        // tslint:disable-next-line:variable-name
        const field_values = [];

        Object.entries(entry).forEach(([key, value]) => {
          const id = fieldMap[key.toLowerCase()];
          if (id) {
            field_values.push({ id, value });
          }
        });

        return {
          field_values,
          pictures: JSON.parse((entry.pictures as string) || '[]'),
          videos: JSON.parse((entry.videos as string) || '[]'),
        };
      });
    }

    const payload = {
      ...(data ? { id: data?.id } : { id: `new_${new Date()}` }),
      control_form_id: formData['formType'],
      comment: formData['comment'],
      latitude: formData['latitude'],
      longitude: formData['longitude'],
      // Testing forms save under control_forms: linked at the form header, never per line.
      map_feature_link: this._linePayloadLink(formData),
      pictures,
      videos,
      lines,
      // Reuse the same dynamic-signers serializer as forms/control-forms. Testing
      // forms have no legacy printed-name controls, so the legacy QA/QC text
      // fallback args are null — only the captured signatures[] array is threaded.
      signatures: DailyReportService.buildSignaturesPayload(null, null, signatures),
      field_values: [
        ...headerFieldValues,
        ...footerFieldValues,
      ],
    };

    this.save.next({
      type: 'forms',
      data: [payload],
    });
  }

  /**
   * Build + emit the save payload for a `display_format='generic'` control form
   * (Generic Composable Forms — "uniform report_form_line", Option U).
   *
   * Storage model — matches the frozen checklist + mobile commit 3ae01b2c:
   *  - Header block answers  -> top-level `field_values` (keyed by header field
   *    id), exactly like header_lines/testing forms store header fields.
   *  - Questions block answers (Y/N/NA) -> `lines`, one report_form_line per
   *    answered question (`field_values = [{ id: <questionFieldId>, value }]`),
   *    EXACTLY like the old checklist + mobile. NOT top-level field_values.
   *  - Table block rows -> `report_form_line` rows (`lines[].field_values`), the
   *    same shape testing/header_lines forms emit.
   *  - Signatures -> the shared buildSignaturesPayload pipeline.
   *
   * Question-lines and table-lines coexist in `lines`; they are distinguished by
   * field id (question id sets vs column id sets never overlap), so the generic
   * renderer's table rehydration/filtering ignores question-lines and vice versa.
   *
   * `fieldValueFields` is the list of HEADER fields whose answers belong in
   * top-level `field_values`. `tableFields` is the union of every table column
   * field across table blocks (maps line controls -> column ids). `lineGroups`
   * is the ordered list of per-row table FormGroups. `questionGroups` is the
   * ordered list of per-block question FormGroups (each keyed
   * `<lineId|new_ts>_<fieldId>` -> { field_values, comment, pictures, videos });
   * answered questions in these become question-lines.
   *
   * Emits on the SAME `save` subject with `type: 'forms'`, so the existing
   * daily-report save + report generation read it with no API change.
   */
  formatGenericForms(
    rootForm: FormGroup,
    fieldValueFields: any[],
    tableFields: any[],
    lineGroups: { group: FormGroup; sectionName: string | null }[],
    questionGroups: FormGroup[],
    data: any,
    signatures: any[] = [],
  ) {
    const formData = linkRawValue(rootForm);

    // tslint:disable-next-line:variable-name
    const field_values = (fieldValueFields || []).map((field: any) => {
      let value = formData[field.name] ?? null;
      // Mirror header field "_others" handling so a free-text "other" answer is
      // stored as the field's value (matching formatForms()).
      if (value === '_others') {
        value = formData[field.name + '_others'] ?? null;
      }
      // Time controls hold the 12-hour display string; storage is 24-hour
      // HH:MM:SS (what the DB holds and what mobile writes).
      if (isTimeField(field)) {
        value = toStoredTime(value);
      }
      return { id: field.id, value };
    });

    // Preserve answers whose field no longer exists on the form. `field_values`
    // above is rebuilt from the CURRENT ui_config fields, so a field removed (or
    // its id changed) after reports were filed would be silently dropped from
    // storage the next time an old report is re-saved — destroying historical
    // data that is merely un-rendered, not obsolete. Mobile already passes
    // unknown ids through untouched (prepareGenericSavePayload only filters
    // display_only/undefined), so carrying them here also keeps the two
    // platforms from producing different rows for the same edit.
    const knownIds = new Set(field_values.map((fv: any) => fv.id));
    (data?.field_values || []).forEach((saved: any) => {
      if (saved?.id != null && !knownIds.has(saved.id)) {
        field_values.push({ id: saved.id, value: saved.value });
      }
    });

    const fieldMap: Record<string, any> = {};
    const fieldByName: Record<string, any> = {};
    (tableFields || []).forEach((field: any) => {
      fieldMap[field.name] = field.id;
      fieldByName[field.name] = field;
    });

    const lines = (lineGroups || []).map(({ group, sectionName }) => {
      const raw = group.getRawValue();
      // tslint:disable-next-line:variable-name
      const lineFieldValues = [];
      Object.entries(raw).forEach(([key, value]) => {
        const id = fieldMap[key];
        if (id) {
          // The `<name>_timePeriod` / `<name>_am_pm` companions carry no field id,
          // so they never reach here; the value control converts back to HH:MM:SS.
          const stored = isTimeField(fieldByName[key]) ? toStoredTime(value) : value ?? null;
          lineFieldValues.push({ id, value: stored ?? null });
        }
      });
      return {
        // Stamp the owning table block's section_name on every row (null for a
        // sectionless block) so rows reopen in the right block — including on
        // mobile, which groups/filters table rows by `line.section_name`.
        section_name: sectionName ?? null,
        field_values: lineFieldValues,
        pictures: JSON.parse(raw.pictures || '[]'),
        videos: JSON.parse(raw.videos || '[]'),
      };
    });

    // Question answers -> report_form_lines (one per ANSWERED question), keyed by
    // the BARE question field id (matching the old checklist + mobile 3ae01b2c).
    // Each question group control is keyed `<lineId|new_ts>_<fieldId>` holding
    // { field_values, comment, pictures, videos } — extract the trailing field id
    // and skip unanswered questions (null/''/undefined field value).
    const questionLines: any[] = [];
    (questionGroups || []).forEach((group: FormGroup) => {
      const raw = group.getRawValue();
      Object.entries(raw).forEach(([controlKey, questionData]: [string, any]) => {
        const value = questionData?.['field_values'];
        if (value === null || value === undefined || value === '') {
          return;
        }
        // The trailing segment of `<lineId|new_ts>_<fieldId>` is the question
        // field id (field ids contain no underscore in this schema).
        const questionFieldId = controlKey.substring(controlKey.lastIndexOf('_') + 1);
        questionLines.push({
          comment: questionData?.['comment'] ?? null,
          pictures: JSON.parse(questionData?.['pictures'] || '[]'),
          videos: JSON.parse(questionData?.['videos'] || '[]'),
          field_values: [{ id: questionFieldId, value }],
        });
      });
    });

    const payload = {
      ...(data ? { id: data?.id } : { id: `new_${new Date()}` }),
      control_form_id: formData['formType'],
      comment: formData['comment'],
      latitude: formData['latitude'],
      longitude: formData['longitude'],
      // Linked at the form header, never per line.
      map_feature_link: this._linePayloadLink(formData),
      pictures: JSON.parse(formData.pictures || '[]'),
      videos: JSON.parse(formData.videos || '[]'),
      // Table rows + question-lines coexist in `lines` (distinguished by field id).
      lines: [...lines, ...questionLines],
      signatures: DailyReportService.buildSignaturesPayload(null, null, signatures),
      // Station-range parity with legacy formatForms: build the same { from, to,
      // alignment } shape from the root form's stationRange* controls (null when
      // none set) so a station-range form keeps its range when flipped to generic.
      station_range: this._buildStationRangePayload(formData),
      field_values,
    };

    this.save.next({
      type: 'forms',
      data: [payload],
    });
  }

  // TODO:DO NOT DELETE CODE NEEDED LATER FOR COMPARISON IF ANY ISSUE ARISES RELATED TO BID ITEMS
  // private _createBidItemPayload(bidItem, lineItem, values) {
  //   let payload = {
  //     ...lineItem,
  //     comment: values[lineItem.id].comment,
  //     pictures: JSON.parse(values[lineItem.id].pictures),
  //     videos: JSON.parse(values[lineItem.id].videos),
  //     latitude: values[lineItem.id].latitude,
  //     longitude: values[lineItem.id].longitude,
  //   };
  //
  //   if (bidItem.rollup === false) {
  //     payload = {
  //       ...payload,
  //       quantity: values[lineItem.id].quantity ? Number(values[lineItem.id].quantity) : null,
  //     };
  //   } else if (bidItem.rollup === true) {
  //     const fieldValues = [];
  //
  //     bidItem?.fields?.forEach((fieldValue) => {
  //       fieldValues.push({
  //         id: fieldValue.id,
  //         value: values?.[lineItem.id]?.[fieldValue.id] ? Number(values?.[lineItem.id]?.[fieldValue.id]) : null,
  //       });
  //     });
  //
  //     payload = {
  //       ...payload,
  //       field_values: fieldValues,
  //     };
  //
  //     if (bidItem.rollup_quantity_editable === true) {
  //       payload = {
  //         ...payload,
  //         quantity: values[lineItem.id].valueType === 'dynamic_entry' ?
  //           '' :
  //           values[lineItem.id].quantity && Number(values[lineItem.id].quantity) || null,
  //       };
  //     }
  //   }
  //
  //   return payload;
  // }

  formatAndSaveInternalTest(headerLevelForm, fieldValueForm, itdToDelete) {
    const headerLevelData = headerLevelForm.getRawValue();
    let fieldLevelData = linkRawValue(fieldValueForm);

    for (const key in fieldLevelData) {
      if (fieldLevelData.hasOwnProperty(key)) {
        fieldLevelData[key] = { ...fieldLevelData[key], id: key };
      }
    }

    fieldLevelData = Object.values(fieldLevelData);
    fieldLevelData = fieldLevelData.map((fieldLevel: any) => {
      // map_feature_link is the line's design context, not a test field: keep it out of field_values.
      const { latitude, longitude, pictures, videos, time, comment, id, station_id, map_feature_link, ...rest } = fieldLevel;
      const fieldValues = Object.entries(rest).map(([key, value]) => ({
        id: key,
        value,
      }));

      return {
        ...(id ? { id } : {}),
        latitude,
        longitude,
        map_feature_link: recordLinkPayload(map_feature_link, latitude, longitude),
        station_id,
        pictures: JSON.parse(pictures),
        videos: JSON.parse(videos),
        comment,
        record_time: time,
        bid_item_id: headerLevelData?.bidItem,
        heading_id: headerLevelData?.heading,
        internal_test_id: headerLevelData?.onSiteTest,
        field_values: fieldValues,
      };
    });

    this.save.next({
      type: 'internalTest',
      data: fieldLevelData,
      options: {
        itdToDelete,
      },
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  /** A form group's link in the api shape: undefined (key dropped) without a control, null when pinless. */
  private _linePayloadLink(values) {
    return recordLinkPayload(values?.map_feature_link, values?.latitude, values?.longitude);
  }

  private _createBidItemPayload(bidItem, lineItem, values) {
    let payload = {
      ...lineItem,
      comment: values[lineItem.id].comment,
      pictures: JSON.parse(values[lineItem.id].pictures),
      videos: JSON.parse(values[lineItem.id].videos),
      latitude: values[lineItem.id].latitude,
      longitude: values[lineItem.id].longitude,
      location: values[lineItem.id]?.location || null,
      station_range: this._buildStationRangePayload(values[lineItem.id]),
      // Always set (overrides the serialized link spread from lineItem): undefined drops out of the JSON.
      map_feature_link: recordLinkPayload(
        values[lineItem.id]?.map_feature_link, values[lineItem.id]?.latitude, values[lineItem.id]?.longitude,
      ),
      ...(values[lineItem.id]['valueType'] === 'manual_entry' ? { quantity: Number(values[lineItem.id].quantity) } : { quantity: 0 }),
      ...(values[lineItem.id]['valueType'] === 'manual_entry' ? { rollup_quantity_manual: true } : { rollup_quantity_manual: false }),
    };

    if (payload.sources.length) {
      const lineItemSources = payload.sources;
      lineItemSources.forEach(source => {
        let key = payload.id;
        if (payload.sources.length > 1) {
          key = `${payload.id}_${source.id}`;
        }

        const formValues = values[key];

        if (bidItem.rollup === false) {
          source.modified_quantity = formValues.quantity ? Number(formValues.quantity) : null;
        } else if (bidItem.rollup === true) {
          source.modified_fields = [];

          bidItem.fields.forEach((field) => {
            source.modified_fields.push({
              [field.name]: formValues[field.id] ? Number(formValues[field.id]) : null,
            });
          });

          if (bidItem.rollup_quantity_editable === true) {
            source.modified_quantity = formValues.valueType === 'dynamic_rollup' ?
              0 :
              formValues.quantity && Number(formValues.quantity) || null;
          }
        }

      });
    } else {
      if (bidItem.rollup === false) {
        payload = {
          ...payload,
          quantity: values[lineItem.id].quantity ? Number(values[lineItem.id].quantity) : null,
        };
      } else if (bidItem.rollup === true) {
        const fieldValues = [];

        bidItem?.fields?.forEach((fieldValue) => {
          fieldValues.push({
            id: fieldValue.id,
            value: values?.[lineItem.id]?.[fieldValue.id] ? Number(values?.[lineItem.id]?.[fieldValue.id]) : null,
          });
        });

        payload = {
          ...payload,
          field_values: fieldValues,
        };

        if (bidItem.rollup_quantity_editable === true) {
          payload = {
            ...payload,
            quantity: values[lineItem.id].valueType === 'dynamic_rollup' ?
              0 :
              values[lineItem.id].quantity && Number(values[lineItem.id].quantity) || null,
          };
        }
      }
    }
    return payload;
  }

  private _buildStationRangePayload(values) {
    const hasStationRange = !!(
      values?.stationRangeFromStation
      || values?.stationRangeFromOffsetType
      || values?.stationRangeFromOffsetDistance
      || values?.stationRangeToStation
      || values?.stationRangeToOffsetType
      || values?.stationRangeToOffsetDistance
      || values?.alignment
    );

    if (!hasStationRange) return null;

    return {
      from: {
        station: values.stationRangeFromStation || null,
        offset_type: values.stationRangeFromOffsetType || null,
        offset_distance: values.stationRangeFromOffsetDistance || null,
      },
      to: {
        station: values.stationRangeToStation || null,
        offset_type: values.stationRangeToOffsetType || null,
        offset_distance: values.stationRangeToOffsetDistance || null,
      },
      alignment: values.alignment || null,
    };
  }

  private _createDeliveryAndUsageBlocks(list, type) {
    if (type === 'deliveryBidItems') {
      return list.map((item) => ({
        id: item.id,
        heading: item.bid_item.item,
        subheading: item.bid_item.description,
        uom: item.bid_item.uom,
        quantity: item.quantity,
        fieldValues: item.field_values,
        latitude: item.latitude,
        longitude: item.longitude,
        location: item.location,
        station_range: item.station_range,
        map_feature_link: item.map_feature_link,
        pictures: item.pictures,
        videos: item.videos,
        comment: item.comment,
        type: 'delivery',
        subType: 'onlyBidItem',
        initialState: item,
      }));
    }

    if (type === 'deliveryMaterials') {
      return list.map((item) => ({
        id: item.id,
        heading: item.material.name,
        uom: item.material.uom,
        quantity: item.received,
        fieldValues: item.field_values,
        latitude: item.latitude,
        longitude: item.longitude,
        location: item.location,
        station_range: item.station_range,
        map_feature_link: item.map_feature_link,
        pictures: item.pictures,
        videos: item.videos,
        comment: item.comment,
        type: 'delivery',
        subType: 'onlyMaterial',
        initialState: item,
      }));
    }

    if (type === 'deliveryBidItemMaterials') {
      const reduced = list.reduce((reducedValue, current) => {
        // GET rows carry the bid_item relation, not bid_item_id: keying on bid_item_id
        // lumped every bid item's materials under one header.
        const bidItemId = relationId(current, 'bid_item');
        if (reducedValue[bidItemId]) {
          reducedValue[bidItemId].push(current);
        } else {
          reducedValue[bidItemId] = [current];
        }

        return reducedValue;
      }, {});

      const resp = [];

      // tslint:disable-next-line:forin
      for (const key in reduced) {
        const data = reduced[key];

        const headerItemIndex = data.findIndex(o => o.bid_item && !o.material && !o.quantity && !o.received && !o.used);

        let headerItem: any;

        if (headerItemIndex > -1) {
          headerItem = (data.splice(headerItemIndex, 1))[0];
        } else {
          headerItem = {
            id: `new_${new Date().getTime()}`,
            bid_item: data[0].bid_item,
            bid_item_id: relationId(data[0], 'bid_item'),
          };
        }

        const payload = {
          id: headerItem.id,
          heading: headerItem.bid_item.item,
          subheading: headerItem.bid_item.description,
          latitude: headerItem.latitude || null,
          longitude: headerItem.longitude || null,
          location: headerItem.location || null,
          station_range: headerItem.station_range || null,
          map_feature_link: headerItem.map_feature_link,
          pictures: headerItem.pictures || [],
          videos: headerItem.videos || [],
          comment: headerItem.comment || null,
          type: 'delivery',
          subType: 'bidItemWithMaterials',
          initialState: headerItem,

          materials: data.filter(o => o.quantity === 0 && o.received !== null && o.used === null)
            .map(item => ({
                id: item.id,
                heading: item.material?.name,
                uom: item.material?.uom,
                quantity: item.received,
                fieldValues: item.field_values,
                latitude: item.latitude,
                longitude: item.longitude,
                location: item.location,
                station_range: item.station_range,
                map_feature_link: item.map_feature_link,
                pictures: item.pictures,
                videos: item.videos,
                comment: item.comment,
                type: 'delivery',
                subType: 'onlyMaterial',
                initialState: item,
              }),
            ),
        };

        if (payload.materials.length) {
          resp.push(payload);
        }
      }

      return resp;
    }

    if (type === 'usageBidItems') {
      return list.map((item) => ({
        id: item.id,
        heading: item.bid_item.item,
        subheading: item.bid_item.description,
        uom: item.bid_item.uom,
        quantity: item.used,
        latitude: item.latitude,
        longitude: item.longitude,
        location: item.location,
        station_range: item.station_range,
        pictures: item.pictures,
        videos: item.videos,
        comment: item.comment,
        type: 'usage',
        subType: 'onlyBidItem',
        initialState: item,
      }));
    }

    if (type === 'usageMaterials') {
      return list.map((item) => ({
        id: item.id,
        heading: item.material.name,
        uom: item.material.uom,
        quantity: item.used,
        fieldValues: item.field_values,
        latitude: item.latitude,
        longitude: item.longitude,
        location: item.location,
        station_range: item.station_range,
        pictures: item.pictures,
        videos: item.videos,
        comment: item.comment,
        type: 'usage',
        subType: 'onlyMaterial',
        initialState: item,
      }));
    }

    if (type === 'usageBidItemMaterials') {
      const reduced = list.reduce((reducedValue, current) => {
        // GET rows carry the bid_item relation, not bid_item_id: keying on bid_item_id
        // lumped every bid item's materials under one header.
        const bidItemId = relationId(current, 'bid_item');
        if (reducedValue[bidItemId]) {
          reducedValue[bidItemId].push(current);
        } else {
          reducedValue[bidItemId] = [current];
        }

        return reducedValue;
      }, {});

      const resp = [];

      // tslint:disable-next-line:forin
      for (const key in reduced) {
        const data = reduced[key];

        const headerItemIndex = data.findIndex(o => o.bid_item && !o.material && !o.quantity && !o.received && !o.used);

        let headerItem: any;

        if (headerItemIndex > -1) {
          headerItem = (data.splice(headerItemIndex, 1))[0];
        } else {
          headerItem = {
            id: `new_${new Date().getTime()}`,
            bid_item: data[0].bid_item,
            bid_item_id: relationId(data[0], 'bid_item'),
          };
        }

        const payload = {
          id: headerItem.id,
          heading: headerItem.bid_item.item,
          subheading: headerItem.bid_item.description,
          latitude: headerItem.latitude || null,
          longitude: headerItem.longitude || null,
          location: headerItem.location || null,
          station_range: headerItem.station_range || null,
          pictures: headerItem.pictures || [],
          videos: headerItem.videos || [],
          comment: headerItem.comment || null,
          type: 'usage',
          subType: 'bidItemWithMaterials',
          initialState: headerItem,

          materials: data.filter(o => o.quantity === 0 && o.received === null && o.used !== null)
            .map(item => ({
                id: item.id,
                heading: item.material.name,
                uom: item.material.uom,
                quantity: item.used,
                fieldValues: item.field_values,
                latitude: item.latitude,
                longitude: item.longitude,
                location: item.location,
                station_range: item.station_range,
                pictures: item.pictures,
                videos: item.videos,
                comment: item.comment,
                type: 'usage',
                subType: 'onlyMaterial',
                initialState: item,
              }),
            ),
        };

        if (payload.materials.length) {
          resp.push(payload);
        }
      }

      return resp;
    }

    return [];
  }

  private removeEmptyLineData(tempLinesData: any) {
    const lines = [];
    tempLinesData.forEach((item) => {
      if (item?.field_values?.length) {
        item.field_values[0].id = item?.field_values?.[0]?.id?.split('_')?.pop();

        if (item?.field_values[0]?.value !== null) {
          lines.push(item);
        }
      }
    });
    return lines;
  }
}
